import type { ChipTone } from '@/design';
import { usStateName } from '@/domains/auth';
import type {
  IdentifierCheckDecisionKind,
  IdentifierCheckReason,
  IdentifierCheckStatus,
  IIdentifierCheckRegistry
} from './api/identifier-check-contracts';

/**
 * A number check's words (SR-487, PFR §4; ruling 15) — survey-api's codes as staff read them, for
 * the Number checks queue and every other place a check is drawn. Each map falls back to the code
 * as spelled: a code a later survey-api sends is a label to look up, never a wrong one.
 */

export const CHECK_STATUSES: readonly { value: IdentifierCheckStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'replaced', label: 'Replaced' }
];

/** Why registration could not corroborate the number. `no_registry` is every NPN and licence:
 *  none is looked up (PFR D8). */
export const CHECK_REASONS: readonly { value: IdentifierCheckReason; label: string }[] = [
  { value: 'not_found', label: 'Not found at FINRA' },
  { value: 'registry_error', label: 'FINRA could not be reached' },
  { value: 'no_registry', label: 'No registry to ask' },
  { value: 'disputed', label: 'Disputed' }
];

export const CHECK_ID_TYPES: readonly { value: string; label: string }[] = [
  { value: 'CRD', label: 'CRD' },
  { value: 'NPN', label: 'NPN' },
  { value: 'INSURANCE_LICENSE', label: 'Licence' }
];

function labelOf(table: readonly { value: string; label: string }[], code: string): string {
  return table.find((entry) => entry.value === code)?.label ?? code;
}

export function checkStatusLabel(status: string): string {
  return labelOf(CHECK_STATUSES, status);
}

export function checkReasonLabel(reason: string): string {
  return labelOf(CHECK_REASONS, reason);
}

/** A check's number as staff read it: "CRD 4821906", "Licence 99-1 (California)". */
export function checkNumberText(check: {
  idType: string;
  value: string;
  issuingState: Nullable<string>;
}): string {
  const number = `${labelOf(CHECK_ID_TYPES, check.idType)} ${check.value}`;
  return check.issuingState ? `${number} (${usStateName(check.issuingState)})` : number;
}

/** An open check is work to do; a closed one is settled either way. */
export function checkStatusTone(status: string): ChipTone {
  return status === 'open' ? 'attention' : 'neutral';
}

/** The three decisions (PFR §4.2), as the pane's buttons and dialogs name them. */
export const CHECK_DECISIONS: readonly { value: IdentifierCheckDecisionKind; label: string }[] = [
  { value: 'verify', label: 'Verify' },
  { value: 'reject', label: 'Reject' },
  { value: 'replace', label: 'Replace' }
];

export function checkDecisionLabel(decision: string): string {
  return labelOf(CHECK_DECISIONS, decision);
}

/** What each decision does to the number, said in its dialog before the press — survey-api's
 *  effects (PFR §4.2), of the participant named. */
export function checkDecisionSays(decision: IdentifierCheckDecisionKind, name: string): string {
  switch (decision) {
    case 'verify':
      return (
        `The number is ${name}’s: it stays on their record — written there first where it is ` +
        'not — marked verified by staff.'
      );
    case 'reject':
      return (
        `The number is not ${name}’s: it is taken off their record and is free for its owner. ` +
        'Their account stays — contact them.'
      );
    case 'replace':
      return `${name}’s number is another one: the number you type takes its place, marked verified by staff.`;
  }
}

/** What NotPermitted says a reader without `responses.annotate` cannot do here. */
export const NOT_PERMITTED_CHECK = 'working number checks';

/**
 * FINRA's latest answer about the participant (`registry`), in words — null where no answer
 * exists: an NPN or a licence, which no registry is asked about (PFR D8), or a CRD before its
 * first answer. `name` null on `ok` is an answer that names nobody (the design's known
 * differences).
 */
export function registryAnswerText(registry: IIdentifierCheckRegistry): string {
  switch (registry.status) {
    case 'ok':
      return registry.name
        ? `FINRA BrokerCheck holds a record: ${registry.name}.`
        : 'FINRA BrokerCheck holds a record that names nobody.';
    case 'not_found':
      return 'FINRA BrokerCheck holds no record.';
    case 'error':
      return 'FINRA BrokerCheck could not be reached.';
    default:
      return `FINRA BrokerCheck answered “${registry.status}”, which this app has no words for.`;
  }
}
